import uniq from 'lodash/uniq';

import type {
	Collection,
	CollectionView,
} from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import type { DocNode } from '@atlaskit/adf-schema/doc';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createActionsHook,
	createSelector,
	createStateHook,
	createContainer,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import type { OutputSelector } from '@atlassian/react-sweet-state/src/utils/create-selector.tsx';
import type { CollectionsFilters } from './CollectionsFilters.types.tsx';
import * as actions from './ControllersCollectionsActions.tsx';
import type { Props, State } from './State.types.tsx';
import {
	filterCollectionByOwnerIds,
	filterCollectionByText,
	filterCollectionByProjectKeys,
} from './filterCollectionByText.tsx';

type Actions = typeof actions;

const initialState: State = {
	collections: {},
	isLoading: true,
	error: undefined,
	descriptions: {},
};

const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'PolarisCollections',
});

export const CollectionsContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	Actions,
	Props
>(Store);

export const useCollectionsActions: HookActionsFunction<BoundActions<State, typeof actions>> =
	createActionsHook(Store);

const getCollections = (state: State) => state.collections;

/** exported for test purposes */
export const getCollectionsList: OutputSelector<State, void, Collection[]> = createSelector(
	getCollections,
	(collections) =>
		Object.values(collections)
			.map((c) => c?.data)
			.filter(Boolean),
);
const getCollectionIds = createSelector(getCollections, (collections) => Object.keys(collections));
const getCollectionsLoading = (state: State) => state.isLoading;
const getCollectionsError = (state: State) => state.error;
const getCollectionsHasError = createSelector(getCollectionsError, (error) => !!error);

const getCollectionsOwnersIds = createSelector(getCollectionsList, (collections) =>
	uniq(collections.map(({ ownerId }) => ownerId).filter(Boolean)),
);

const getFilteredCollections = createSelector(
	(state: State) => getCollectionsList(state),
	(_, { text }: CollectionsFilters) => text,
	(_, { ownerIds }: CollectionsFilters) => ownerIds,
	(_, { projects }: CollectionsFilters) => projects,
	(collections, text, ownerIds, projects) =>
		collections
			? collections.filter(
					(collection) =>
						filterCollectionByText(text)(collection) &&
						filterCollectionByOwnerIds(ownerIds)(collection) &&
						filterCollectionByProjectKeys(projects)(collection),
				)
			: [],
);

const getCollectionsCount = createSelector(getCollectionsList, (collections) => collections.length);

export const useCollectionsOwnersIds: HookStateFunction<string[], void> = createStateHook(Store, {
	selector: getCollectionsOwnersIds,
});
export const useFilteredCollections: HookStateFunction<Collection[], CollectionsFilters> =
	createStateHook(Store, { selector: getFilteredCollections });
export const useCollections: HookStateFunction<
	Record<
		string,
		| {
				data: Collection | undefined;
				isLoading?: boolean;
				error?: Error;
		  }
		| undefined
	>,
	void
> = createStateHook(Store, { selector: getCollections });
export const useCollectionsLoading: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: getCollectionsLoading,
});
export const useCollectionsHasError: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: getCollectionsHasError,
});
export const useCollectionsError: HookStateFunction<Error | undefined, void> = createStateHook(
	Store,
	{ selector: getCollectionsError },
);
export const useCollectionsCount: HookStateFunction<number, void> = createStateHook(Store, {
	selector: getCollectionsCount,
});
export const useCollectionIds: HookStateFunction<string[], void> = createStateHook(Store, {
	selector: getCollectionIds,
});

const getCollectionWithMeta = createSelector(
	getCollections,
	(_, { collectionUUID }: { collectionUUID: string | undefined }) => collectionUUID,
	(collections, collectionUUID) => (collectionUUID ? collections[collectionUUID] : undefined),
);
const getCollection = createSelector(getCollectionWithMeta, (collection) => collection?.data);
const getCollectionName = createSelector(getCollection, (collection) => collection?.name);
const getCollectionEmoji = createSelector(getCollection, (collection) => collection?.emoji);
const getCollectionJql = createSelector(getCollection, (collection) => collection?.jql);
const getCollectionViews = createSelector(getCollection, (collection) => collection?.views || []);
const hasFetchedCollectionViews = createSelector(getCollection, (collection) =>
	Array.isArray(collection?.views),
);

const getIsCollectionLoading = createSelector(
	getCollectionWithMeta,
	(collection) => !!collection?.isLoading,
);
const getCollectionProjectKeys = createSelector(
	getCollection,
	(collection) => collection?.projectKeys,
);
const getCollectionFiltersJql = createSelector(
	getCollection,
	(collection) => collection?.filtersJql,
);
const getProjectCollectionsCount = createSelector(getCollections, (collections) =>
	Object.values(collections)
		.flatMap((collection) => collection?.data?.projectKeys)
		.filter((key): key is string => !!key)
		.reduce((acc: Record<string, number>, projectKey) => {
			acc[projectKey] = (acc[projectKey] ?? 0) + 1;
			return acc;
		}, {}),
);
const getError = createSelector(getCollectionWithMeta, (collection) => collection?.error);
const hasError = createSelector(getError, (error) => !!error);
const getCollectionViewsOwnersIds = createSelector(getCollectionViews, (views) =>
	uniq(views.map(({ ownerId }) => ownerId).filter(Boolean)),
);
const getCollectionView = createSelector(
	getCollectionViews,
	(_, { viewUUID }: { viewUUID: string | undefined; collectionUUID: string | undefined }) =>
		viewUUID,
	(views, viewUUID) => (viewUUID ? views.find((view) => view.uuid === viewUUID) : undefined),
);
const getCollectionViewTitle = createSelector(getCollectionView, (view) => view?.title);
const getCollectionViewsCount = createSelector(
	getCollection,
	(collection) => collection?.views?.length || collection?.viewsCount,
);
const getCollectionOwnerId = createSelector(getCollection, (collection) => collection?.ownerId);

const getDescriptions = (state: State) => state.descriptions;
const getCollectionDescriptionWithMeta = createSelector(
	getDescriptions,
	(_, { collectionUUID }: { collectionUUID: string | undefined }) => collectionUUID,
	(descriptions, collectionUUID) => (collectionUUID ? descriptions[collectionUUID] : undefined),
);
const getCollectionDescription = createSelector(
	getCollectionDescriptionWithMeta,
	(descriptionWithMeta) => descriptionWithMeta?.description,
);
const getIsCollectionDescriptionLoading = createSelector(
	getCollectionDescriptionWithMeta,
	(descriptionWithMeta) => !!descriptionWithMeta?.isLoading,
);
const getCollectionDescriptionHasError = createSelector(
	getCollectionDescriptionWithMeta,
	(descriptionWithMeta) => !!descriptionWithMeta?.error,
);

export const useCollection: HookStateFunction<
	Collection | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollection });
export const useCollectionName: HookStateFunction<
	string | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionName });
export const useCollectionEmoji: HookStateFunction<
	string | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionEmoji });
export const useCollectionJql: HookStateFunction<
	string | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionJql });
export const useCollectionViews: HookStateFunction<
	CollectionView[],
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionViews });
export const useCollectionLoading: HookStateFunction<
	boolean,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getIsCollectionLoading });

export const useCollectionError: HookStateFunction<
	Error | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getError });
export const useCollectionHasError: HookStateFunction<
	boolean,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: hasError });
export const useCollectionViewsOwnersIds: HookStateFunction<
	string[],
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionViewsOwnersIds,
});
export const useCollectionView: HookStateFunction<
	CollectionView | undefined,
	{
		viewUUID: string | undefined;
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionView });
export const useCollectionViewTitle: HookStateFunction<
	string | undefined,
	{
		viewUUID: string | undefined;
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionViewTitle });
export const useCollectionViewsCount: HookStateFunction<
	number | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionViewsCount,
});
export const useHasFetchedCollectionViews: HookStateFunction<
	boolean,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: hasFetchedCollectionViews,
});
export const useCollectionOwnerId: HookStateFunction<
	string | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, { selector: getCollectionOwnerId });
export const useCollectionDescription: HookStateFunction<
	DocNode | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionDescription,
});
export const useCollectionProjectKeys: HookStateFunction<
	string[] | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionProjectKeys,
});
export const useCollectionFiltersJql: HookStateFunction<
	string | undefined,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionFiltersJql,
});
export const useProjectCollectionsCount: HookStateFunction<
	Record<string, number>,
	void
> = createStateHook(Store, {
	selector: getProjectCollectionsCount,
});
export const useIsCollectionDescriptionLoading: HookStateFunction<
	boolean,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getIsCollectionDescriptionLoading,
});
export const useCollectionDescriptionHasError: HookStateFunction<
	boolean,
	{
		collectionUUID: string | undefined;
	}
> = createStateHook(Store, {
	selector: getCollectionDescriptionHasError,
});
